import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { BehaviorSubject, Observable, race } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    CreateWidget,
    CreateWidgetSuccess,
    DeleteWidget,
    DeleteWidgetFailure,
    DeleteWidgetSuccess,
    FetchWidgets,
    FetchWidgetsFailure,
    FetchWidgetsSuccess,
    UpdateWidget,
    UpdateWidgetSuccess,
} from '../actions/index';
import { MaxBrainWidget } from '../models/widget.model';
import { WidgetState } from '../models/widget.state';
import { WidgetsState } from '../models/widgets.state';

@Injectable()
export class WidgetService {
    @Select(WidgetState.getEntities)
    widget$: Observable<MaxBrainWidget[]>;

    @Select(WidgetsState.getFilteredEntities)
    filteredWidget$: Observable<MaxBrainWidget[]>;

    private _newWidgetSubject = new BehaviorSubject<MaxBrainWidget>(null);
    public newWidgetSubject$ = this._newWidgetSubject.asObservable();

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper) {}

    getWidgets(): MaxBrainWidget[] {
        return this._store.selectSnapshot(WidgetsState.getFilteredEntities);
    }

    getWidgetsById(id: string): MaxBrainWidget {
        return WidgetState.getEntity(this._store.selectSnapshot(WidgetState), id);
    }

    async fetchWidgets(): Promise<boolean> {
        this._store.dispatch(new FetchWidgets());

        return await this._actionHelper.race(FetchWidgetsSuccess, FetchWidgetsFailure).toPromise();
    }

    createWidget(widget: MaxBrainWidget): void {
        this._store.dispatch(new CreateWidget(widget));

        this._action$.pipe(ofActionDispatched(CreateWidgetSuccess), take(1)).subscribe((action: CreateWidgetSuccess) => {
            this._newWidgetSubject.next(action.payload);
        });
    }

    updateWidget(widget: MaxBrainWidget): void {
        this._store.dispatch(new UpdateWidget(widget));

        this._action$.pipe(ofActionDispatched(UpdateWidgetSuccess), take(1)).subscribe((action: UpdateWidgetSuccess) => {
            this._newWidgetSubject.next(action.payload);
        });
    }

    deleteWidget(widgetId: string): Promise<any> {
        this._store.dispatch(new DeleteWidget(widgetId));

        return race(
            this._action$.pipe(
                ofActionDispatched(DeleteWidgetSuccess),
                map((action: DeleteWidgetSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(DeleteWidgetFailure),
                map(() => null)
            )
        )
            .pipe()
            .toPromise();
    }
}
